import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ModalWrapper from '../shared/ModalWrapper';
import { PLAN_REQUIREMENTS_MODES } from '../../utils/constants';
import { CARD_COPY } from '../../utils/plan-requirements-copy';

// Skips react-responsive-modal's exit animation for action-triggered closes.
const INSTANT_CLOSE_ANIMATION_MS = 0;

/**
 * Renders copy with `**bold**` markers as <strong> segments, keeping the
 * copy module free of JSX so wording stays trivially editable.
 *
 * @param {string} text
 * @returns {(string|JSX.Element)[]}
 */
function renderBold(text) {
    return text
        .split('**')
        .map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part));
}

/**
 * Renders a requirement body, turning its single `**bold**` destination marker
 * into a keyboard-accessible action button when the item resolves to a
 * navigation target and a handler is wired up. Semantics come only from
 * `item.action`; the marker text is never parsed for a destination. Without an
 * action or handler (`onActivate` is undefined) the body falls back to plain
 * <strong> so nothing that cannot navigate ever looks clickable.
 *
 * @param {import('../../utils/plan-requirements').RequirementItem} item
 * @param {((action: import('../../utils/plan-requirements').RequirementAction) => void)|undefined} onActivate
 *   activates the item's action (closing the modal, then routing); undefined
 *   when no handler is wired
 * @returns {(string|JSX.Element)[]}
 */
function renderRequirementBody(item, onActivate) {
    const isActionable = Boolean(item.action) && typeof onActivate === 'function';
    if (!isActionable) {
        return renderBold(item.body);
    }

    return item.body.split('**').map((part, index) =>
        index % 2 === 1 ? (
            <button
                key={index}
                type="button"
                className="plan-requirements-action"
                data-test-btn={`requirement-action-${item.limit}`}
                onClick={() => onActivate(item.action)}
            >
                {part}
            </button>
        ) : (
            part
        ),
    );
}

function ArrowIcon() {
    return (
        <svg
            className="plan-requirements-arrow"
            width="14"
            height="12"
            viewBox="0 0 14 12"
            fill="none"
            aria-hidden="true"
        >
            <path
                d="M2 6h10M8 2l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/**
 * The plan requirements modal for a blocked plan, including its card
 * trigger ("View requirements"). Presentational: it renders copy and, where a
 * requirement resolves to a navigation target, a link-like action button that
 * closes the modal and delegates the structured action to `onRequirementAction`
 * (the parent owns Admin vs. Billing-route navigation). Action-triggered closes
 * skip the exit animation so it cannot freeze mid-flight and flash on return
 * (see `instantClose` below). No re-check, no footer.
 * Two shapes, decided by the view model's mode:
 * - standard: a list of every blocker with a bold settings/support destination
 * - audience: focused copy naming the recommended plan, followed by any
 *   concurrent non-audience requirements
 *
 * @param {object} props
 * @param {import('../../utils/plan-requirements').RequirementsViewModel} props.viewModel
 * @param {string} props.productName - for stable test ids
 * @param {(action: import('../../utils/plan-requirements').RequirementAction) => void} [props.onRequirementAction]
 *   invoked with an item's action when its button is activated; when absent,
 *   actionable copy renders as plain <strong> so nothing looks clickable
 * @returns {JSX.Element}
 */
export default function PlanRequirementsModal({ viewModel, productName, onRequirementAction }) {
    // ModalWrapper requires controlled open state: without it, its
    // modalIsOpen-sync effect closes the modal right after the trigger opens it
    const [modalIsOpen, setModalIsOpen] = useState(false);
    // When an action closes the modal, Ghost Admin hides the billing iframe
    // almost immediately to navigate to the requested settings screen. That
    // freezes the modal's CSS exit animation mid-flight; on return to the
    // overlay the frozen animation resumes and the modal visibly flashes.
    // Making action-triggered closes instant leaves nothing to replay. Escape,
    // overlay-click, and trigger-open keep their normal animation.
    const [instantClose, setInstantClose] = useState(false);
    const headingId = `requirements-heading-${productName.toLowerCase()}`;
    const headingRef = useRef(null);

    // Opening again clears the instant-close flag so the modal animates in and
    // its next non-action close animates normally.
    const handleSetModalIsOpen = (open) => {
        if (open) {
            setInstantClose(false);
        }
        setModalIsOpen(open);
    };

    const onActivate =
        typeof onRequirementAction === 'function'
            ? (action) => {
                  // Close first (instantly) so navigation never fights the exit
                  // animation, then hand the structured action to the parent.
                  setInstantClose(true);
                  setModalIsOpen(false);
                  onRequirementAction(action);
              }
            : undefined;

    const trigger = (
        <button
            className="gh-btn gh-btn-block plan-requirements-trigger"
            data-test-btn={`requirements-${productName}`}
        >
            {CARD_COPY.viewRequirements}
            <ArrowIcon />
        </button>
    );

    return (
        <ModalWrapper
            title={viewModel.title}
            openModalTrigger={trigger}
            modalIsOpen={modalIsOpen}
            setModalIsOpen={handleSetModalIsOpen}
            ariaLabelledby={headingId}
            focusTrapped={true}
            initialFocusRef={headingRef}
            // undefined lets react-responsive-modal apply its default duration
            animationDuration={instantClose ? INSTANT_CLOSE_ANIMATION_MS : undefined}
        >
            <div
                className="plan-requirements-modal"
                data-testid={`requirements-modal-${productName}`}
            >
                <h3
                    ref={headingRef}
                    className="plan-requirements-heading"
                    id={headingId}
                    tabIndex={-1}
                >
                    {viewModel.title}
                </h3>
                <p className="plan-requirements-sub">{renderBold(viewModel.sub)}</p>
                {viewModel.mode === PLAN_REQUIREMENTS_MODES.AUDIENCE && viewModel.note && (
                    <p className="plan-requirements-note">{renderBold(viewModel.note)}</p>
                )}
                {viewModel.items.length > 0 && (
                    <ul className="plan-requirements-list">
                        {viewModel.items.map((item) => (
                            <li className="plan-requirements-item" key={item.limit}>
                                <h4 className="plan-requirements-title">{item.title}</h4>
                                <p className="plan-requirements-detail">
                                    {renderRequirementBody(item, onActivate)}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </ModalWrapper>
    );
}

PlanRequirementsModal.propTypes = {
    viewModel: PropTypes.shape({
        mode: PropTypes.oneOf(Object.values(PLAN_REQUIREMENTS_MODES)).isRequired,
        title: PropTypes.string.isRequired,
        sub: PropTypes.string.isRequired,
        note: PropTypes.string,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                limit: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                body: PropTypes.string.isRequired,
                // Structured navigation target, or null for informational items
                action: PropTypes.shape({
                    kind: PropTypes.string.isRequired,
                    destination: PropTypes.string,
                    route: PropTypes.string,
                }),
            }),
        ).isRequired,
    }).isRequired,
    productName: PropTypes.string.isRequired,
    onRequirementAction: PropTypes.func,
};
